import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWorkflowAnalyticsRequest,
  fetchWorkflowListRequest
} from "../../../store/slices/workflow";
import {
  aggregateSessionStats,
  buildAiInsightMessage,
  buildDailySessionRows,
  buildHeatMapList,
  buildNodePerformanceRows,
  computeSessionMetrics,
  formatAnalyticsMonthLabel,
  getCurrentAnalyticsMonth,
  toAnalyticsCount
} from "../utils/workflowAnalyticsUtils";
import {
  getMaxVersion,
  getPreferredWorkflowVersion,
  getWorkflowVersionSelectOptions
} from "../utils/workflowListUtils";

export function useWorkflowAnalyticsPage() {
  const dispatch = useDispatch();
  const { workflows, loading: listLoading } = useSelector((state) => state.workflowList);
  const { analytics, loading: analyticsLoading, error } = useSelector((state) => state.workflowAnalytics);

  const [workflowId, setWorkflowIdState] = useState("");
  const [version, setVersion] = useState("");
  const [month, setMonth] = useState(getCurrentAnalyticsMonth());

  const selectedWorkflow = useMemo(
    () => workflows.find((item) => item.workflowId === workflowId),
    [workflows, workflowId]
  );

  const versionOptions = useMemo(
    () => (selectedWorkflow ? getWorkflowVersionSelectOptions(selectedWorkflow) : []),
    [selectedWorkflow]
  );

  const setWorkflowId = (nextWorkflowId) => {
    setWorkflowIdState(nextWorkflowId);
    const workflow = workflows.find((item) => item.workflowId === nextWorkflowId);
    if (!workflow) {
      setVersion("");
      return;
    }
    const latestVersion = getMaxVersion(workflow)?.version ?? getPreferredWorkflowVersion(workflow);
    setVersion(latestVersion != null ? String(latestVersion) : "");
  };

  useEffect(() => {
    if (!workflows.length) {
      dispatch(fetchWorkflowListRequest({ operation: "LIST_WORKFLOWS", payload: {} }));
    }
  }, [dispatch, workflows.length]);

  useEffect(() => {
    if (workflowId || !workflows.length) return;
    const first = workflows[0];
    const latestVersion = getMaxVersion(first)?.version ?? getPreferredWorkflowVersion(first);
    setWorkflowIdState(first.workflowId);
    setVersion(latestVersion != null ? String(latestVersion) : "");
  }, [workflows, workflowId]);

  useEffect(() => {
    if (!selectedWorkflow || !versionOptions.length) return;
    const isValid = versionOptions.some((opt) => opt.value === version);
    if (!isValid) setVersion(versionOptions[0].value);
  }, [selectedWorkflow, version, versionOptions]);

  useEffect(() => {
    if (!workflowId || !version || !month) return;
    dispatch(fetchWorkflowAnalyticsRequest({
      workflowId,
      version: Number(version),
      month
    }));
  }, [dispatch, workflowId, version, month]);

  const sessionTotals = useMemo(
    () => (analytics ? aggregateSessionStats(analytics.sessionStats, month) : null),
    [analytics, month]
  );

  const nodeRows = useMemo(
    () => (analytics ? buildNodePerformanceRows(analytics, month) : []),
    [analytics, month]
  );

  const heatMapRows = useMemo(
    () => (analytics ? buildHeatMapList(analytics) : []),
    [analytics]
  );

  const dailyRows = useMemo(
    () => (analytics ? buildDailySessionRows(analytics.sessionStats, month) : []),
    [analytics, month]
  );

  const metrics = sessionTotals ? computeSessionMetrics(sessionTotals) : null;
  const liveSessionCount = toAnalyticsCount(analytics?.liveSessionCount);
  const insightMessage = metrics
    ? buildAiInsightMessage(metrics, nodeRows, liveSessionCount)
    : "";
  const hasData = Boolean(
    analytics && (
      sessionTotals?.started > 0 ||
      sessionTotals?.completed > 0 ||
      sessionTotals?.abandoned > 0 ||
      nodeRows.length > 0 ||
      heatMapRows.some((row) => row.liveCount > 0) ||
      liveSessionCount > 0
    )
  );
  const isLoading = listLoading || (analyticsLoading && !analytics);

  return {
    workflows,
    workflowId,
    setWorkflowId,
    version,
    setVersion,
    month,
    setMonth,
    versionOptions,
    listLoading,
    isLoading,
    error,
    hasData,
    workflowName: selectedWorkflow?.name ?? "Workflow Analytics",
    monthLabel: formatAnalyticsMonthLabel(month),
    liveSessionCount,
    metrics,
    nodeRows,
    heatMapRows,
    dailyRows,
    insightMessage
  };
}
